/* Accessibility strings, anti-spam text, and browser-capability
 * fallbacks — deliberately NOT in the CMS.
 *
 * The rule: if emptying a field produces a defect with no visual signal,
 * it doesn't belong in the CMS. A blanked aria-label degrades the site
 * for screen-reader users while looking perfectly fine to everyone else,
 * including the editor who blanked it. These also have to change in
 * lockstep with markup, which content editing does not.
 *
 * Several of these currently ship in English regardless of the selected
 * language ("Skip to content", "Go back", "Close navigation"). They are
 * translated here — that is a bug fix, not a migration.
 *
 * Resolve with `t()` from lib/localize.js exactly like CMS fields, so
 * call sites read the same either way.
 */

export const A11Y = {
  skipToContent: { english: 'Skip to content', french: 'Aller au contenu' },
  goBack: { english: 'Go back', french: 'Retour' },
  openNavigation: { english: 'Open navigation', french: 'Ouvrir le menu' },
  closeNavigation: { english: 'Close navigation', french: 'Fermer le menu' },
  homeLink: { english: 'Soquelec — Home', french: 'Soquelec — Accueil' },
  inquiryLink: { english: 'My Inquiry', french: 'Ma Demande' },
  decreaseQuantity: { english: 'Decrease quantity', french: 'Diminuer la quantité' },
  increaseQuantity: { english: 'Increase quantity', french: 'Augmenter la quantité' },
  removeFromInquiry: { english: 'Remove from inquiry', french: 'Retirer de la demande' },
  socialMedia: { english: 'Social media', french: 'Réseaux sociaux' },
  changeLanguage: {
    english: 'Change language to French',
    french: 'Changer la langue pour l’anglais'
  },

  /* Honeypot. A spam countermeasure must never be editable, and must
     never appear in a translation export where someone might "fix" it. */
  honeypot: {
    english: 'Don’t fill this out if you’re human:',
    french: 'Don’t fill this out if you’re human:'
  },

  /* Describes a browser capability, not a message from Soquelec. */
  videoUnsupported: {
    english: 'Your browser does not support HTML5 video.',
    french: 'Votre navigateur ne prend pas en charge la vidéo HTML5.'
  }
};

/* Accessible names for the hero CTAs, keyed by destination rather than
   authored alongside the button label. Keeps the label editable while
   the accessible name stays correct and in sync with where the button
   actually goes. */
export const CTA_ARIA = {
  '/about': { english: 'About Soquelec', french: 'À propos de Soquelec' },
  '/contact': { english: 'Contact Soquelec', french: 'Contacter Soquelec' },
  '/equipments': { english: 'Equipment offered', french: 'Instrumentation offerte' },
  '/consumables': { english: 'Consumables catalog', french: 'Catalogue de fournitures' },
  '/consumables/all': { english: 'All consumables', french: 'Toutes les fournitures' },
  '/manufacturers': { english: 'Our manufacturers', french: 'Nos manufacturiers' },
  '/inquiry': { english: 'My inquiry', french: 'Ma demande' },
  '/': { english: 'Soquelec home', french: 'Accueil Soquelec' }
};

/* Accessible names for social icons, keyed by the platform value stored
   on the socialLink object. */
export const SOCIAL_ARIA = {
  facebook: 'Facebook',
  x: 'X',
  linkedin: 'LinkedIn'
};

export default A11Y;
